import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { getMyJourney, GetMyJourneyOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowRight, Trophy, Star } from 'lucide-react';

type Data = GetMyJourneyOutputType;

const categoryIcons: Record<string, string> = {
  'Pub Induction': '🍺', 'Front of House': '🍷', 'Back of House': '🍳',
  'Food Safety & Hygiene': '🥗', 'Health, Safety & Fire': '🛡️',
  'Alcohol Law & Licensing': '⚖️', 'Management & Leadership': '👑',
};

export default function MyJourney() {
  const { user } = useAuth();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getMyJourney({}).then(setData).finally(() => setLoading(false));
  }, [user]);

  if (loading) return (
    <div className="p-8 space-y-6">
      <Skeleton className="h-32 w-full" />
      <div className="grid grid-cols-3 gap-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
    </div>
  );

  return (
    <div className="bg-background">
      {/* Hero Banner */}
      <section className="bg-primary px-8 py-10">
        <p className="text-primary-foreground/70 text-sm mb-1 uppercase tracking-widest font-medium">Welcome back</p>
        <h1 className="text-3xl font-bold text-primary-foreground mb-2">
          {user?.firstName ? `Hi ${user.firstName} 👋` : 'Your Training Journey'}
        </h1>
        <p className="text-primary-foreground/80 text-sm">
          {data?.earnedBadges && data.earnedBadges.length > 0
            ? `🏆 ${data.earnedBadges.length} category certificate${data.earnedBadges.length > 1 ? 's' : ''} earned — keep going!`
            : 'Complete each module to earn your competency certificates.'}
        </p>
      </section>

      <div className="px-8 py-6 space-y-8">
        {/* Continue Card */}
        {data?.nextModule && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Continue where you left off</p>
            <Link to={`/modules/${data.nextModule.id}`}>
              <div className="border border-border bg-card p-5 flex items-center justify-between hover:border-primary transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 bg-primary flex items-center justify-center text-2xl">
                    {categoryIcons[data.nextModule.categoryName] || '📚'}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{data.nextModule.title}</p>
                    <p className="text-sm text-muted-foreground">{data.nextModule.categoryName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-primary text-sm font-medium">
                  {data.nextModule.currentStep > 0 ? 'Continue' : 'Start'} <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Progress by category */}
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Your progress</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data?.categories.filter(c => c.totalModules > 0).map(cat => {
              const pct = cat.totalModules > 0 ? Math.round((cat.completedModules / cat.totalModules) * 100) : 0;
              const isComplete = cat.signedOffModules === cat.totalModules && cat.totalModules > 0;
              return (
                <div key={cat.id} className={`border bg-card p-4 ${isComplete ? 'border-primary' : 'border-border'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{categoryIcons[cat.name] || '📚'}</span>
                      <span className="text-sm font-medium text-foreground">{cat.name}</span>
                    </div>
                    {isComplete && <Trophy className="w-4 h-4 text-accent" />}
                  </div>
                  <div className="h-1.5 bg-muted w-full mb-1.5">
                    <div className="h-1.5 bg-primary transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground">{cat.completedModules}/{cat.totalModules} complete{cat.signedOffModules > 0 && ` · ${cat.signedOffModules} signed off`}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Badges */}
        {data?.earnedBadges && data.earnedBadges.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Earned certificates</p>
            <div className="flex flex-wrap gap-3">
              {data.earnedBadges.map(badge => (
                <div key={badge} className="flex items-center gap-2 bg-primary/10 border border-primary/30 px-3 py-2">
                  <Star className="w-4 h-4 text-accent fill-accent" />
                  <span className="text-sm font-medium text-foreground">{badge}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
